/**
 * Benutzer — the SEP directory, with tenant and role editable in place.
 *
 * Both writes go through to SEP (`PUT /api/assigned-users`), which owns
 * identities. Our row only follows if SEP accepts, so the table can never claim
 * an assignment SEP does not have.
 *
 * Users the directory import found have no SEP id and are read-only until an
 * admin adds them to SEP's `assigned_users` with the row's own action (doc 06).
 */
import { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';

import type { ApiError } from '@/api/client';
import { api } from '@/api/client';
import { queryKeys, useAssignUser, useKickoutUser, useTenants } from '@/api/hooks';
import { useIdentity } from '@/auth/AuthGate';
import { useEnvironment } from '@/environment/EnvironmentProvider';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState, ErrorState, PageHeader } from '@/components/states';
import { AssignUserDialog } from './AssignUserDialog';
import { KickoutDialog } from './KickoutDialog';
import type { Page, UserSummary } from '@/types/api';

const PAGE_SIZE = 50;

interface UserPatch {
  username: string;
  tenant?: string;
  sepRole?: string;
}

/** A tenant move changes which licenses apply, so it is confirmed first. */
interface PendingMove {
  user: UserSummary;
  tenant: string;
}

interface KickoutButtonProps {
  user: UserSummary;
  isSelf: boolean;
  busy: boolean;
  onClick: () => void;
}

/**
 * The only irreversible control on this page.
 *
 * Offered for *every* row, including the read-only ones. Mandant and Rolle are
 * disabled without a `sepUserId` because `PUT /api/assigned-users` addresses a
 * row by that id; kickout addresses the account by username, so it works for
 * the users the directory import discovered as well.
 *
 * Not offered for the signed-in admin: the server refuses that anyway, and a
 * button whose only outcome is an error message should not be clickable.
 */
function KickoutButton({ user, isSelf, busy, onClick }: KickoutButtonProps) {
  const label = `${user.username} endgültig aus SEP löschen`;
  return (
    <Tooltip title={isSelf ? 'Das eigene Konto lässt sich hier nicht löschen.' : label}>
      {/* Wrapped: a disabled button fires no events, so the tooltip needs a
          live element to hang off. */}
      <span>
        <IconButton
          size="small"
          color="error"
          disabled={isSelf || busy}
          onClick={onClick}
          aria-label={label}
        >
          <PersonRemoveOutlinedIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}

interface AssignButtonProps {
  user: UserSummary;
  busy: boolean;
  onClick: () => void;
}

/**
 * Offered only on read-only rows. Inactive ones get it disabled: SEP no longer
 * lists them, and the server refuses to assign them.
 */
function AssignButton({ user, busy, onClick }: AssignButtonProps) {
  const inactive = user.status !== 'active';
  const label = `${user.username} bearbeitbar machen`;
  return (
    <Tooltip title={inactive ? 'Von SEP nicht mehr aufgeführt.' : label}>
      <span>
        <IconButton
          size="small"
          color="primary"
          disabled={inactive || busy}
          onClick={onClick}
          aria-label={label}
        >
          <LockOpenOutlinedIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
}

export function UsersPage() {
  const environment = useEnvironment();
  const identity = useIdentity();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
  const [pendingKickout, setPendingKickout] = useState<UserSummary | null>(null);
  const [pendingAssign, setPendingAssign] = useState<UserSummary | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(
    null,
  );

  const params = new URLSearchParams({
    limit: String(PAGE_SIZE),
    offset: String(page * PAGE_SIZE),
  });
  if (search) params.set('q', search);

  const { data, isPending, error, refetch } = useQuery<Page<UserSummary>, ApiError>({
    queryKey: queryKeys.users(environment, params.toString()),
    queryFn: ({ signal }) => api.get<Page<UserSummary>>(`/users?${params.toString()}`, signal),
  });

  const { data: tenants } = useTenants();
  const { data: roles } = useQuery<string[], ApiError>({
    queryKey: queryKeys.userRoles(environment),
    queryFn: ({ signal }) => api.get<string[]>('/users/roles', signal),
  });

  const update = useMutation<UserSummary, ApiError, UserPatch>({
    mutationFn: ({ username, ...body }) =>
      api.patch<UserSummary>(`/users/${encodeURIComponent(username)}`, body),
    onSuccess: (updated) => {
      setToast({
        message: `${updated.username}: Mandant ${updated.tenant}, Rolle ${updated.sepRole ?? '—'}.`,
        severity: 'success',
      });
    },
    onError: (mutationError) => {
      setToast({ message: mutationError.message, severity: 'error' });
    },
    onSettled: () => {
      setBusy(null);
      // Effective access may have changed for this user, so the matrix and the
      // tenant lists are stale too.
      void queryClient.invalidateQueries({ queryKey: ['env', environment, 'users'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.matrix(environment) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tenants(environment) });
    },
  });

  const apply = (patch: UserPatch) => {
    setBusy(patch.username);
    update.mutate(patch);
  };

  const kickout = useKickoutUser();

  /**
   * The dialog stays open on failure and closes only on success.
   *
   * Everywhere else in this app a dialog closes on confirm and reports the
   * outcome in a toast. Here the outcome matters too much for a message that
   * disappears after eight seconds: "did that go through?" has no second way of
   * being answered once SEP has forgotten the account.
   */
  const confirmKickout = (confirmUsername: string, reason: string) => {
    if (!pendingKickout) return;
    kickout.mutate(
      { username: pendingKickout.username, confirmUsername, reason },
      {
        onSuccess: (result) => {
          setPendingKickout(null);
          kickout.reset();
          setToast({
            message: `${result.username} wurde in SEP gelöscht. Der Eintrag hier bleibt als inaktiv erhalten.`,
            severity: 'success',
          });
        },
      },
    );
  };

  const closeKickout = () => {
    setPendingKickout(null);
    kickout.reset();
  };

  const assign = useAssignUser();

  /** Same as kickout: open on failure, closed and reported on success. */
  const confirmAssign = () => {
    if (!pendingAssign) return;
    assign.mutate(pendingAssign.username, {
      onSuccess: (result) => {
        setPendingAssign(null);
        assign.reset();
        setToast({
          message: result.createdInSep
            ? `${result.username} ist jetzt bearbeitbar (SEP-ID ${result.sepUserId}).`
            : `${result.username} war in SEP bereits zugewiesen und ist jetzt bearbeitbar (SEP-ID ${result.sepUserId}).`,
          severity: 'success',
        });
      },
    });
  };

  const closeAssign = () => {
    setPendingAssign(null);
    assign.reset();
  };

  return (
    <Box>
      <PageHeader
        title="Benutzer"
        description="Aus SEP importiert. Mandant und Rolle werden direkt in SEP geändert."
      />
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      <Alert severity="info" sx={{ mb: 2 }}>
        Änderungen an Mandant und Rolle werden nach SEP geschrieben. Ein Mandantenwechsel
        ändert sofort, welche Lizenzen für diesen Benutzer gelten.
      </Alert>

      <TextField
        placeholder="E-Mail suchen…"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setPage(0);
        }}
        sx={{ mb: 2, width: { xs: '100%', md: 320 } }}
      />

      {isPending ? (
        <Skeleton variant="rectangular" height={320} />
      ) : !data || data.items.length === 0 ? (
        <EmptyState title="Keine Benutzer" description="Für diese Suche gibt es keine Treffer." />
      ) : isMobile ? (
        // Four columns, two of them dropdowns, do not fit a phone. Stacked
        // cards keep the full-width selects usable with a thumb.
        <Stack spacing={1.5}>
          {data.items.map((user) => {
            const saving = busy === user.username;
            // Discovered through /api/get-tenant-resources, which returns no SEP id.
            // PUT /api/assigned-users addresses a row by that id, so there is nothing
            // to write against — see doc 06.
            const readOnly = user.sepUserId === null;
            return (
              <Card key={user.username}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {user.username}
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        {saving ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Chip
                            label={user.status === 'active' ? 'aktiv' : 'inaktiv'}
                            variant="outlined"
                            color={user.status === 'active' ? 'success' : 'default'}
                          />
                        )}
                        {readOnly && (
                          <AssignButton
                            user={user}
                            busy={saving}
                            onClick={() => setPendingAssign(user)}
                          />
                        )}
                        <KickoutButton
                          user={user}
                          isSelf={user.username === identity.user}
                          busy={saving}
                          onClick={() => setPendingKickout(user)}
                        />
                      </Stack>
                    </Stack>

                    {/* Said once, plainly. Two greyed-out dropdowns with no
                        explanation read as a bug; the reason is not the user's
                        fault and not something they can fix here. */}
                    {readOnly && (
                      <Typography variant="caption" color="text.secondary">
                        Nur lesbar — dieser Benutzer stammt aus dem SEP-Verzeichnis
                        und hat keine SEP-ID. Über «bearbeitbar machen» wird er in SEP
                        zugewiesen; danach lassen sich Mandant und Rolle ändern.
                      </Typography>
                    )}

                    <TextField
                      select
                      label="Mandant"
                      size="small"
                      fullWidth
                      value={user.tenant}
                      disabled={saving || !tenants || readOnly}
                      onChange={(event) => {
                        const next = event.target.value;
                        if (next !== user.tenant) setPendingMove({ user, tenant: next });
                      }}
                    >
                      {!tenants?.some((tenant) => tenant.key === user.tenant) && (
                        <MenuItem value={user.tenant}>{user.tenant}</MenuItem>
                      )}
                      {tenants?.map((tenant) => (
                        <MenuItem key={tenant.key} value={tenant.key}>
                          {tenant.displayName}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      label="SEP-Rolle"
                      size="small"
                      fullWidth
                      value={user.sepRole ?? ''}
                      disabled={saving || !roles || readOnly}
                      onChange={(event) => {
                        const next = event.target.value;
                        if (next !== (user.sepRole ?? '')) {
                          apply({ username: user.username, sepRole: next });
                        }
                      }}
                    >
                      {user.sepRole && !roles?.includes(user.sepRole) && (
                        <MenuItem value={user.sepRole}>{user.sepRole}</MenuItem>
                      )}
                      {roles?.map((role) => (
                        <MenuItem key={role} value={role}>
                          {role}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
          <TablePagination
            component="div"
            count={data.total}
            page={page}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            onPageChange={(_event, next) => setPage(next)}
          />
        </Stack>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Benutzer</TableCell>
                <TableCell sx={{ width: 260 }}>Mandant</TableCell>
                <TableCell sx={{ width: 170 }}>SEP-Rolle</TableCell>
                <TableCell sx={{ width: 110 }}>Status</TableCell>
                {/* Visually empty — a heading over a single icon costs width and
                    says nothing the tooltip does not. Still announced, so the
                    column is not nameless to a screen reader. */}
                <TableCell sx={{ width: 88 }} align="right">
                  <Box
                    component="span"
                    sx={{
                      position: 'absolute',
                      width: 1,
                      height: 1,
                      overflow: 'hidden',
                      clip: 'rect(0 0 0 0)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Aktionen
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map((user) => {
                const saving = busy === user.username;
                // Discovered through /api/get-tenant-resources, which returns no SEP id.
                // PUT /api/assigned-users addresses a row by that id, so there is nothing
                // to write against — see doc 06.
                const readOnly = user.sepUserId === null;
                return (
                  <TableRow key={user.username} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        {user.username}
                        {saving && <CircularProgress size={14} />}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        fullWidth
                        value={user.tenant}
                        disabled={saving || !tenants || readOnly}
                        slotProps={{
                          input: { 'aria-label': `Mandant von ${user.username}` },
                        }}
                        onChange={(event) => {
                          const next = event.target.value;
                          if (next !== user.tenant) setPendingMove({ user, tenant: next });
                        }}
                      >
                        {/* The current value is always offered, even if the list
                            has not loaded or no longer contains it — otherwise
                            the Select renders blank and looks broken. */}
                        {!tenants?.some((tenant) => tenant.key === user.tenant) && (
                          <MenuItem value={user.tenant}>{user.tenant}</MenuItem>
                        )}
                        {tenants?.map((tenant) => (
                          <MenuItem key={tenant.key} value={tenant.key}>
                            {tenant.displayName}
                            {tenant.displayName !== tenant.key && ` (${tenant.key})`}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        fullWidth
                        value={user.sepRole ?? ''}
                        disabled={saving || !roles || readOnly}
                        slotProps={{
                          input: { 'aria-label': `SEP-Rolle von ${user.username}` },
                        }}
                        onChange={(event) => {
                          const next = event.target.value;
                          if (next !== (user.sepRole ?? '')) {
                            apply({ username: user.username, sepRole: next });
                          }
                        }}
                      >
                        {user.sepRole && !roles?.includes(user.sepRole) && (
                          <MenuItem value={user.sepRole}>{user.sepRole}</MenuItem>
                        )}
                        {roles?.map((role) => (
                          <MenuItem key={role} value={role}>
                            {role}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          user.status === 'active'
                            ? 'Wird von SEP aufgeführt.'
                            : 'Von SEP nicht mehr aufgeführt. Ausnahmen und Protokoll bleiben erhalten.'
                        }
                      >
                        <Chip
                          label={user.status === 'active' ? 'aktiv' : 'inaktiv'}
                          variant="outlined"
                          color={user.status === 'active' ? 'success' : 'default'}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {readOnly && (
                        <AssignButton
                          user={user}
                          busy={saving}
                          onClick={() => setPendingAssign(user)}
                        />
                      )}
                      <KickoutButton
                        user={user}
                        isSelf={user.username === identity.user}
                        busy={saving}
                        onClick={() => setPendingKickout(user)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data.total}
            page={page}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            onPageChange={(_event, next) => setPage(next)}
          />
        </TableContainer>
      )}

      <ConfirmDialog
        open={pendingMove !== null}
        title="Mandant wechseln?"
        confirmLabel="Mandant wechseln"
        message={
          pendingMove && (
            <>
              <strong>{pendingMove.user.username}</strong> wird in SEP von{' '}
              <strong>{pendingMove.user.tenant}</strong> nach{' '}
              <strong>{pendingMove.tenant}</strong> verschoben. Damit gelten sofort die
              Lizenzen des neuen Mandanten.
            </>
          )
        }
        onCancel={() => setPendingMove(null)}
        onConfirm={() => {
          if (pendingMove) {
            apply({ username: pendingMove.user.username, tenant: pendingMove.tenant });
          }
          setPendingMove(null);
        }}
      />

      <KickoutDialog
        key={pendingKickout?.username ?? 'none'}
        user={pendingKickout}
        pending={kickout.isPending}
        error={kickout.error}
        onCancel={closeKickout}
        onConfirm={confirmKickout}
      />

      <AssignUserDialog
        user={pendingAssign}
        pending={assign.isPending}
        error={assign.error}
        onCancel={closeAssign}
        onConfirm={confirmAssign}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={toast?.severity === 'error' ? 8000 : 4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity={toast?.severity ?? 'success'}
          variant="filled"
          onClose={() => setToast(null)}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
